import { useListenHover } from '@/utils'
import { MouseEvent, ReactNode, useEffect, useRef, JSX } from 'react'
import { createPortal } from 'react-dom'
import { Typography } from '../../atoms'
import { positionAtCursor } from './helpers'
import { Container, HoverContent } from './styles'

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  dark?: boolean
}

export const Tooltip = ({
  content,
  children,
  dark,
}: TooltipProps): JSX.Element => {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const cursorPosition = useRef({ x: 0, y: 0 })

  const { isOpen, onMouseEnter, onMouseLeave } = useListenHover()

  const updatePosition = () => {
    if (tooltipRef.current) {
      positionAtCursor(
        tooltipRef.current,
        cursorPosition.current.x,
        cursorPosition.current.y,
      )
    }
  }

  const handleMouseEnter = (event: MouseEvent) => {
    cursorPosition.current = { x: event.clientX, y: event.clientY }
    onMouseEnter(event)
  }

  const handleMouseMove = (event: MouseEvent) => {
    cursorPosition.current = { x: event.clientX, y: event.clientY }
    updatePosition()
  }

  // Position immediately once the tooltip mounts, using the cursor
  // coordinates captured on hover, then track further movement above.
  useEffect(() => {
    if (isOpen) updatePosition()
  }, [isOpen])

  return (
    <Container onMouseLeave={onMouseLeave}>
      {isOpen &&
        createPortal(
          <HoverContent ref={tooltipRef} dark={dark}>
            {content}
          </HoverContent>,
          document.body,
        )}
      <div onMouseEnter={handleMouseEnter} onMouseMove={handleMouseMove}>
        <Typography variant="p2Body">{children}</Typography>
      </div>
    </Container>
  )
}
